import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Search, X, SlidersHorizontal, Loader2, Clock, ArrowRight } from 'lucide-react';
import { COURSES as MOCK_COURSES, CATEGORIES, LEVELS, AWARDING_BODIES } from '../data/mock';
import { useSiteLanguage } from '../context/SiteLanguageContext';
import { normalizeCourse } from '../utils/normalizeCourse';
import { getInstalmentPlan } from '../utils/instalments';
import Seo from '../components/Seo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Courses() {
  const { t } = useSiteLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const [allCourses, setAllCourses]         = useState([]);
  const [coursesLoading, setCoursesLoading] = useState(true);
  const [query, setQuery]                   = useState(searchParams.get('search') || '');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || 'All Courses');
  const [selectedLevel, setSelectedLevel]   = useState('');
  const [selectedBody, setSelectedBody]     = useState('');
  const [sortBy, setSortBy]                 = useState('featured');
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  useEffect(() => {
    axios.get(`${API}/courses`)
      .then(res => setAllCourses(res.data.map(normalizeCourse)))
      .catch(() => setAllCourses(MOCK_COURSES))
      .finally(() => setCoursesLoading(false));
  }, []);

  useEffect(() => {
    const cat    = searchParams.get('category');
    const search = searchParams.get('search');
    if (cat)    setSelectedCategory(cat);
    if (search) setQuery(search);
  }, [searchParams]);

  const filtered = allCourses.filter(c => {
    const matchCat   = selectedCategory === 'All Courses' || c.category === selectedCategory;
    const matchLevel = !selectedLevel || c.level === selectedLevel;
    const matchBody  = !selectedBody  || c.awardingBody === selectedBody;
    const matchQuery = !query || c.title.toLowerCase().includes(query.toLowerCase()) || c.category.toLowerCase().includes(query.toLowerCase());
    return matchCat && matchLevel && matchBody && matchQuery;
  }).sort((a, b) => {
    if (sortBy === 'price-asc')  return a.price - b.price;
    if (sortBy === 'price-desc') return b.price - a.price;
    return (b.featured ? 1 : 0) - (a.featured ? 1 : 0);
  });

  const clearFilters = () => {
    setSelectedCategory('All Courses');
    setSelectedLevel('');
    setSelectedBody('');
    setQuery('');
    setSearchParams({});
  };

  const activeFiltersCount =
    (selectedCategory !== 'All Courses' ? 1 : 0) +
    (selectedLevel ? 1 : 0) +
    (selectedBody  ? 1 : 0) +
    (query         ? 1 : 0);

  const filterBtnClass = (active) =>
    `w-full text-left px-3 py-2 rounded-xl text-sm font-semibold transition-all ${
      active ? 'bg-[#4B286D] text-white shadow-sm' : 'text-gray-600 hover:bg-[#F3ECF9] hover:text-[#4B286D]'
    }`;

  const SidebarFilters = () => (
    <div>
      <div className="flex items-center justify-between mb-5">
        <h3 className="font-bold text-[#1A1A2E] text-sm">Filters</h3>
        {activeFiltersCount > 0 && (
          <button onClick={clearFilters} className="text-xs text-[#DE00A5] font-bold hover:underline flex items-center gap-1">
            <X size={12} /> Clear all
          </button>
        )}
      </div>

      {/* Category */}
      <div className="mb-6">
        <h4 className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-3">{t('courses.filter.category')}</h4>
        <div className="space-y-0.5">
          {CATEGORIES.map(cat => (
            <button key={cat} onClick={() => setSelectedCategory(cat)} className={filterBtnClass(selectedCategory === cat)}>
              {cat}
            </button>
          ))}
        </div>
      </div>

      {/* Level */}
      <div className="mb-6 border-t border-gray-100 pt-5">
        <h4 className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-3">{t('courses.filter.level')}</h4>
        <div className="space-y-0.5">
          <button onClick={() => setSelectedLevel('')} className={filterBtnClass(!selectedLevel)}>
            {t('courses.filter.allLevels')}
          </button>
          {LEVELS.map(lvl => (
            <button key={lvl} onClick={() => setSelectedLevel(lvl)} className={filterBtnClass(selectedLevel === lvl)}>
              {lvl}
            </button>
          ))}
        </div>
      </div>

      {/* Awarding Body */}
      <div className="border-t border-gray-100 pt-5">
        <h4 className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-3">{t('courses.filter.body')}</h4>
        <div className="space-y-0.5">
          <button onClick={() => setSelectedBody('')} className={filterBtnClass(!selectedBody)}>
            {t('courses.filter.allBodies')}
          </button>
          {AWARDING_BODIES.map(body => (
            <button key={body} onClick={() => setSelectedBody(body)} className={filterBtnClass(selectedBody === body)}>
              {body}
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F7F7F7]">
      <Seo
        title="Browse Online Courses"
        description="Browse accredited OTHM and QUALIFI online diplomas from Level 2 to Level 7 in health & social care, business, education, IT, law, and childcare."
        path="/courses"
      />

      {/* Page header — purple, FutureLearn style */}
      <div className="bg-gradient-to-br from-[#4B286D] to-[#3B1D57] text-white py-14 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-white/5 rounded-full translate-x-1/3 -translate-y-1/2 blur-3xl pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 relative">
          <p className="text-[#DE00A5] text-sm font-semibold mb-1">{t('courses.hero.eyebrow')}</p>
          <h1 className="text-3xl lg:text-4xl font-bold mb-3 tracking-tight">{t('courses.hero.title')}</h1>
          <p className="text-white/60 max-w-xl text-sm">{t('courses.hero.desc')}</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Search + sort bar */}
        <div className="flex flex-col sm:flex-row gap-3 mb-8">
          <div className="flex-1 relative">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" size={17} />
            <input
              type="text"
              placeholder={t('courses.search.placeholder')}
              value={query}
              onChange={e => setQuery(e.target.value)}
              className="w-full pl-11 pr-10 py-3 rounded-xl border border-[#E5E5E5] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#4B286D]/20 focus:border-[#4B286D] shadow-sm"
            />
            {query && (
              <button onClick={() => setQuery('')} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                <X size={15} />
              </button>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={() => setShowMobileFilters(!showMobileFilters)}
              className={`lg:hidden flex items-center gap-2 px-4 py-3 rounded-xl border text-sm font-bold transition-all ${
                showMobileFilters
                  ? 'bg-[#4B286D] text-white border-[#4B286D]'
                  : 'bg-white text-gray-700 border-[#E5E5E5] hover:border-[#4B286D]/30'
              }`}
            >
              <SlidersHorizontal size={15} />
              {t('courses.filters.btn')}
              {activeFiltersCount > 0 && (
                <span className="w-5 h-5 rounded-full bg-[#DE00A5] text-white text-xs flex items-center justify-center">{activeFiltersCount}</span>
              )}
            </button>
            <select
              value={sortBy}
              onChange={e => setSortBy(e.target.value)}
              className="px-4 py-3 rounded-xl border border-[#E5E5E5] bg-white text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#4B286D]/20 focus:border-[#4B286D] cursor-pointer shadow-sm font-semibold"
            >
              <option value="featured">{t('courses.sort.featured')}</option>
              <option value="price-asc">{t('courses.sort.priceLow')}</option>
              <option value="price-desc">{t('courses.sort.priceHigh')}</option>
            </select>
          </div>
        </div>

        {/* Mobile filter panel */}
        {showMobileFilters && (
          <div className="lg:hidden bg-white rounded-2xl border border-[#E5E5E5] shadow-sm p-6 mb-6">
            <SidebarFilters />
          </div>
        )}

        {/* Two-column layout */}
        <div className="flex gap-8 items-start">
          {/* Desktop sidebar */}
          <aside className="hidden lg:block w-60 shrink-0 sticky top-24">
            <div className="bg-white rounded-2xl border border-[#E5E5E5] shadow-sm p-6">
              <SidebarFilters />
            </div>
          </aside>

          {/* Main content */}
          <main className="flex-1 min-w-0">
            {/* Mobile category pills */}
            <div className="lg:hidden flex gap-2 overflow-x-auto pb-2 mb-5 scrollbar-hide">
              {CATEGORIES.map(cat => (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-bold transition-all border ${
                    selectedCategory === cat
                      ? 'bg-[#4B286D] text-white border-[#4B286D]'
                      : 'bg-white text-gray-600 border-[#E5E5E5] hover:border-[#4B286D]/30 hover:text-[#4B286D]'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>

            {/* Results info */}
            <div className="flex items-center justify-between mb-5">
              {coursesLoading ? (
                <span className="flex items-center gap-2 text-sm text-gray-500">
                  <Loader2 size={14} className="animate-spin text-[#4B286D]" /> {t('courses.loading')}
                </span>
              ) : (
                <p className="text-sm text-gray-500">
                  Showing <span className="font-bold text-[#4B286D]">{filtered.length}</span> course{filtered.length !== 1 ? 's' : ''}
                  {selectedCategory !== 'All Courses' && <> in <span className="font-bold text-[#4B286D]">{selectedCategory}</span></>}
                </p>
              )}
            </div>

            {/* Course grid */}
            {!coursesLoading && filtered.length === 0 ? (
              <div className="text-center py-24 bg-white rounded-2xl border border-[#E5E5E5]">
                <Search size={40} className="text-gray-200 mx-auto mb-3" />
                <p className="text-gray-500 font-semibold mb-1">{t('courses.notFound')}</p>
                <p className="text-sm text-gray-400 mb-4">Try adjusting your filters or search term</p>
                <button onClick={clearFilters} className="text-[#DE00A5] font-bold hover:underline text-sm">{t('courses.clearFilters')}</button>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
                {filtered.map(course => (
                  <CourseCard key={course.id || course.slug} course={course} />
                ))}
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}

function CourseCard({ course }) {
  const { t } = useSiteLanguage();
  const plan = getInstalmentPlan(course);
  return (
    <Link
      to={`/courses/${course.slug}`}
      className="group flex flex-col bg-white rounded-2xl overflow-hidden border border-[#E5E5E5] hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5"
    >
      {/* Image */}
      <div className="relative h-44 overflow-hidden bg-gray-100 shrink-0">
        <img
          src={course.image}
          alt={course.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        {course.onSale && (
          <span className="absolute top-3 left-3 bg-[#DE00A5] text-white text-xs font-bold px-2.5 py-1 rounded-full shadow-sm">
            {t('shared.badge.sale')}
          </span>
        )}
      </div>

      {/* Body */}
      <div className="p-5 flex flex-col flex-1">
        <div className="flex items-center gap-2 mb-2.5">
          <span className="text-xs font-semibold text-[#4B286D] bg-[#F3ECF9] px-2.5 py-1 rounded-full">
            {course.awardingBody}
          </span>
          <span className="text-xs text-gray-400 flex items-center gap-1">
            <Clock size={11} />{course.duration}
          </span>
        </div>

        <h3 className="font-bold text-[#1A1A2E] text-sm leading-snug mb-auto group-hover:text-[#4B286D] transition-colors line-clamp-2 min-h-[2.5rem]">
          {course.title}
        </h3>

        <div className="mt-3 mb-3">
          <span className="text-xs text-gray-500 border border-gray-200 px-2.5 py-1 rounded-full">
            {course.level}
          </span>
        </div>

        <div className="flex items-center justify-between pt-3 border-t border-gray-100">
          <div>
            <span className="text-[#1A1A2E] font-extrabold text-xl">£{course.price}</span>
            {course.onSale && (
              <span className="text-gray-400 line-through text-xs ml-1.5">£{course.originalPrice}</span>
            )}
            {plan && (
              <p className="text-xs text-gray-400 mt-0.5">
                {t('shared.instalments.short', 'or {count} × £{amount}')
                  .replace('{count}', plan.count)
                  .replace('{amount}', plan.perInstalment)}
              </p>
            )}
          </div>
          <span className="shrink-0 text-xs font-bold bg-[#DE00A5] text-white px-3.5 py-1.5 rounded-full group-hover:bg-[#B80089] transition-colors">
            Enrol →
          </span>
        </div>
      </div>
    </Link>
  );
}
